/**
 * EmployeeLoadingFallback — picks the right loading view for the current
 * employee-app route.
 *
 * - On the HomeView routes (`/user`, `/user/sparks`, `/user/sparks/upcoming`,
 *   `/user/sparks/complete`) → render `EmployeeAppSkeleton`, which previews
 *   HomeView's header + tabs + spark cards.
 *
 * - On every other employee route (`/user/wallet`, `/user/profile`,
 *   `/user/help`, spark detail, etc.) → render a plain centred
 *   `PageLoading`. The HomeView-shaped skeleton would be misleading on
 *   those screens (no tabs, different layout), so we fall back to the
 *   smaller, layout-agnostic spinner instead.
 *
 * Use this anywhere we previously hard-coded `<EmployeeAppSkeleton />` as a
 * loading view: `AppRouter` `/user` Suspense fallback, `EmployeeRouter`
 * Suspense fallback, and `ConditionalRoute` (employee context).
 */
import { FC } from 'react';
import { useLocation } from 'react-router-dom';

import PageLoading from '@components/layout/PageLoading';

import EmployeeAppSkeleton from './EmployeeAppSkeleton';
import { isEmployeeHomeRoute } from './isEmployeeHomeRoute';

const EmployeeLoadingFallback: FC = () => {
    const { pathname } = useLocation();
    if (isEmployeeHomeRoute(pathname)) {
        return <EmployeeAppSkeleton />;
    }
    return <PageLoading />;
};

export default EmployeeLoadingFallback;
